<template>
  <main class="patient-advance-details">
    <router-link
      v-bind:to="navigationToInvoiceList"
      class="patient-advance-details__button-back"
    >
      Назад
    </router-link>
    <h1 class="patient-advance-details__title">Операции с авансом</h1>
    <div class="patient-advance-details__detailization">
      <ul
        v-if="cashboxes.length > 0"
        class="patient-advance-details__detailization__cashboxes">
        <li
          v-for="cashbox in cashboxes"
          v-bind:key="cashbox.id"
          class="patient-advance-details__detailization__cashbox"
        >
          <b>{{ cashbox.name }}: </b> {{ cashbox.balance_title }}
        </li>
      </ul>
      <div class="patient-advance-details__table-wrapper">
        <table class="patient-advance-details__table">
          <thead class="patient-advance-details__table__head">
            <tr>
              <th
                class="patient-advance-details__table__cell"
                v-bind:key="`table-advance-details:column-${columnIndex}`"
                v-for="(title, columnIndex) in columns"
              >{{ title }}</th>
              <th />
            </tr>
          </thead>
          <tbody v-if="rows.length > 0">
            <tr
              v-bind:key="`table-advance-details:row-${rowIndex}`"
              v-for="(row, rowIndex) in rows"
            >
              <td
                class="patient-advance-details__table__cell"
                v-bind:key="`table-advance-details:row-${rowIndex}:cell-${cellIndex}`"
                v-for="(title, cellIndex) in row.cells"
              >
                <router-link
                  v-if="cellIndex === 1 && row.invoice_id"
                  :to="{ name: 'ClinicPanelPatientInvoice', params: { invoiceId: row.invoice_id } }"
                >
                  {{ title }}
                </router-link>
                <div
                  v-else-if="cellIndex === 4 && row.actions.includes('payment-mode-changing')"
                  class="patient-advance-details__table__cell__select"
                >
                  <the-select
                    v-bind:options="modesForChangeAdvanceRecharge"
                    v-bind:value="row.payment_mode_id"
                    v-on:input="onChangePaymentMode(row.id, $event)"
                  />
                </div>
                <div
                  v-else-if="cellIndex === 5
                    && row.actions.includes('edit-advance-withdrawal-comment')">
                  <v-textarea
                    v-bind:maxlength="255"
                    v-bind:value="title"
                    v-on:change="onChangeCommentForAdvanceWithdrawal(row.id, $event)"
                    no-resize
                    rows="1"
                    auto-grow
                    hide-details
                  />
                </div>
                <div
                  v-else-if="cellIndex === 5
                    && row.actions.includes('edit-advance-recharge-comment')">
                  <v-textarea
                    v-bind:maxlength="255"
                    v-bind:value="title"
                    v-on:change="onChangeCommentForAdvanceRecharge(row.id, $event)"
                    no-resize
                    rows="1"
                    auto-grow
                    hide-details
                  />
                </div>
                <div v-else>
                  {{ title }}
                </div>
              </td>
              <td v-if="row.actions.includes('print-advance-recharge-slip')">
                <button
                  class="patient-advance-details__button-print"
                  title="Распечатать квитанцию о пополнении аванса пациентом"
                  type="button"
                  v-on:click="onPrintAdvanceRechargeSlip(row.id)" />
              </td>
              <td v-if="row.actions.includes('print-advance-withdrawal-slip')">
                <button
                  class="patient-advance-details__button-print"
                  title="Распечатать квитанцию о списании аванса"
                  type="button"
                  v-on:click="onPrintAdvanceWithdrawalSlip(row.id)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="isEmpty" class="patient-advance-details__empty">
        <span>Операций не найдено</span>
      </div>
    </div>
    <iframe
      class="patient-advance-details__print"
      ref="printView"
      title="printView"
    />
  </main>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import notifierMixin from '@/mixins/notifierMixin';
import debounce from 'lodash.debounce';

export default {
  components: {
    TheSelect,
  },
  computed: {
    isEmpty() {
      return this.isLoaded && this.rows.length === 0;
    },
    patientId() {
      return +this.$route.params.id;
    },
    navigationToInvoiceList() {
      return {
        name: 'ClinicPanelPatientInvoiceList',
        params: { id: this.patientId },
      };
    },
  },
  created() {
    this.fetchAdvanceDetails();
  },
  data() {
    return {
      cashboxes: [],
      columns: [],
      isLoaded: false,
      modesForChangeAdvanceRecharge: [],
      rows: [],
    };
  },
  methods: {
    /**
     * @description Обработать открытие детализации аванса.
     * @returns {void}
     */
    fetchAdvanceDetails() {
      this
        .$invoiceGateway
        .viewPatientAdvanceDetails(this.patientId)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach((details) => {
            this.rows = details.rows;
            this.cashboxes = details.cashboxes;
            this.columns = details.columns;
            this.modesForChangeAdvanceRecharge = details.modes_for_change_advance_recharge;
          });
        })
        .finally(() => { this.isLoaded = true; });
    },
    /**
     * @description Обработать изменения способа оплаты.
     * @param {number} operationId Идентификатор операции.
     * @param {string} mode        Способ оплаты.
     * @returns {void}
     */
    onChangePaymentMode(operationId, mode) {
      this
        .$invoiceGateway
        .changePaymentModeForAdvanceRechargeOperation(
          operationId,
          mode,
        )
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.fetchAdvanceDetails();
            this.notifySuccess(title);
          });
        });
    },
    /**
     * @description Обработать изменения комментария пополнения аванса.
     * @param {number} operationId Идентификатор операции.
     * @param {string} comment     Комментарий.
     * @returns {void}
     */
    onChangeCommentForAdvanceWithdrawal: debounce(async function _(operationId, comment) {
      this
        .$invoiceGateway
        .changeCommentForAdvanceWithdrawalOperation(
          operationId,
          comment,
        )
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.fetchAdvanceDetails();
            this.notifySuccess(title);
          });
        });
    }, 1000),
    /**
     * @description Обработать изменения комментария списания аванса.
     * @param {number} operationId Идентификатор операции.
     * @param {string} comment     Комментарий.
     * @returns {void}
     */
    onChangeCommentForAdvanceRecharge: debounce(async function _(operationId, comment) {
      this
        .$invoiceGateway
        .changeCommentForAdvanceRechargeOperation(
          operationId,
          comment,
        )
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.fetchAdvanceDetails();
            this.notifySuccess(title);
          });
        });
    }, 1000),
    onPrintAdvanceWithdrawalSlip(operationId) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$invoiceGateway
        .printAdvanceWithdrawalSlip(operationId)
        .then((result) => result.fold(onFail, this.printSlip));
    },
    onPrintAdvanceRechargeSlip(operationId) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$invoiceGateway
        .printAdvanceRechargeSlip(operationId)
        .then((result) => result.fold(onFail, this.printSlip));
    },
    /**
     * @description Распечатать квитанцию.
     * @param {any} response
     * @returns {void}
     */
    printSlip(response) {
      const blob = new Blob([response], { type: 'text/html' });
      const container = this.$refs.printView;
      container.setAttribute('src', URL.createObjectURL(blob));
      container.onload = () => {
        container.contentWindow.print();
      };
    },
  },
  mixins: [notifierMixin],
  name: 'ClinicPanelPatientAdvanceDetailList',
};
</script>

<style lang="scss" scoped>
.patient-advance-details {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 38px 20px 48px 23px;
  overflow-y: auto;
  &__button-back {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    text-decoration: none;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  &__button-print {
    background: center / contain no-repeat url("@/assets/img/print-grey.svg");
    font-size: 0;
    height: 14px;
    width: 14px;
    &:hover {
      background-image: url("@/assets/img/print.svg");
    }
  }
  &__detailization {
    display: grid;
    row-gap: 10px;
    overflow: auto;
    &__cashboxes {
      display: grid;
      row-gap: 8px;
      padding: 8px 0;
      font-size: 16px;
      list-style: none;
    }
  }
  &__empty {
    padding: 10px;
    text-align: center;
  }
  &__table-wrapper {
    width: 100%;
    overflow: auto;
  }
  &__table {
    position: relative;
    border-collapse: collapse;
    width: 100%;
    min-width: 820px;
    font-size: 14px;
    &__head {
      border-bottom: 2px solid #0489e0;
    }
    th {
      position: sticky;
      top: 0;
      padding: 10px 8px;
      text-align: left;
      background-color: #ffffff;
      z-index: 1;
      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        border-bottom: 2px solid var(--action-color);
      }
    }
    tr {
      text-align: left;
      border-bottom: 1px solid #eaeff3;
    }
    &__cell {
      padding: 10px;
      & &__select {
        width: fit-content;
        transform: translateX(-0.5rem);
        ::v-deep select {
          padding-inline: 0.5rem 3rem;
          border: none;
          background-size: 13px 13px;
          &:focus {
            border: none;
            outline: none;
          }
        }
      }
      &.actions {
        text-align: right;
      }
    }
  }
  &__title {
    color: #000000;
    margin: 0 0 27px 0;
  }
  &__print {
    display: none;
  }
}
@media screen and (max-width: 480px) {
  .patient-advance-details {
    &__empty {
      padding: 5px;
      text-align: center;
    }
    &__table {
      &__cell {
        padding: 5px;
      }
    }
  }
}
</style>
